import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import {
  Layers, FlaskConical, Waves, RefreshCw, Play, Settings,
  Search, Loader2, CheckCircle, XCircle, Clock, Activity, Zap, MapPin
} from "lucide-react";
import { authFetch } from "@/hooks/use-auth";
import ModuleHeader from "@/components/ModuleHeader";

function fmt(n: number | null | undefined) {
  if (n == null) return "—";
  return n.toLocaleString("pt-BR");
}
function fmtDate(d: string | null | undefined) {
  if (!d) return "Nunca";
  return new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}
function fmtDur(s: number | null | undefined) {
  if (!s) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
}
function fmtEta(elapsed: number, prog: number, total: number) {
  if (!prog || !total || prog >= total) return "—";
  const rem = (total - prog) / (prog / elapsed);
  if (!isFinite(rem)) return "—";
  const m = Math.floor(rem / 60), s = Math.round(rem % 60);
  return m > 0 ? `~${m}m ${s}s` : `~${s}s`;
}

interface LogEntry { ts: number; msg: string; }

export default function SgbPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [syncActive, setSyncActive] = useState(false);
  const [logLines, setLogLines] = useState<LogEntry[]>([]);
  const [startTime, setStartTime] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const logRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Consulta completa
  const [latConsulta, setLatConsulta] = useState("");
  const [lonConsulta, setLonConsulta] = useState("");
  const [raioConsulta, setRaioConsulta] = useState("100");
  const [consultaResult, setConsultaResult] = useState<any>(null);
  const [consultaLoading, setConsultaLoading] = useState(false);

  // Geocronologia list
  const [geoRocha, setGeoRocha] = useState("");
  const [geoMetodo, setGeoMetodo] = useState("");
  const [geoBusca, setGeoBusca] = useState("");
  const [geoPage, setGeoPage] = useState(0);
  const GEO_LIMIT = 50;

  // Config
  const [cfgEnabled, setCfgEnabled] = useState(true);
  const [cfgDays, setCfgDays] = useState("90");

  const { data: status, refetch: refetchStatus, isFetching: statusFetching } = useQuery({
    queryKey: ["/api/sgb/status"],
    queryFn: () => authFetch("/api/sgb/status"),
    refetchInterval: syncActive ? 1000 : 30000,
  });

  const { data: bacias } = useQuery({
    queryKey: ["/api/sgb/bacias"],
    queryFn: () => authFetch("/api/sgb/bacias"),
  });

  const { data: geoList, isLoading: geoLoading } = useQuery({
    queryKey: ["/api/sgb/geocronologia/lista", geoRocha, geoMetodo, geoBusca, geoPage],
    queryFn: () =>
      authFetch(`/api/sgb/geocronologia/lista?rocha=${encodeURIComponent(geoRocha)}&metodo=${encodeURIComponent(geoMetodo)}&busca=${encodeURIComponent(geoBusca)}&limit=${GEO_LIMIT}&offset=${geoPage * GEO_LIMIT}`),
  });

  const { data: syncLog } = useQuery({
    queryKey: ["/api/sgb/sync-log"],
    queryFn: () => authFetch("/api/sgb/sync-log"),
    refetchInterval: syncActive ? 5000 : false,
  });

  const { data: config } = useQuery({
    queryKey: ["/api/sgb/config"],
    queryFn: () => authFetch("/api/sgb/config"),
  });

  const syncMutation = useMutation({
    mutationFn: (tipo: string) => authFetch(`/api/sgb/sync?tipo=${tipo}`, { method: "POST" }),
    onSuccess: (data: any) => {
      toast({ title: "Sync iniciado", description: data.message });
      setSyncActive(true);
      setStartTime(Date.now());
      setLogLines([{ ts: Date.now(), msg: "Sync iniciado..." }]);
      queryClient.invalidateQueries({ queryKey: ["/api/sgb/status"] });
    },
    onError: (err: any) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  const configMutation = useMutation({
    mutationFn: (cfg: { enabled: boolean; intervalDays: number }) =>
      authFetch("/api/sgb/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cfg),
      }),
    onSuccess: () => {
      toast({ title: "Configuração salva" });
      queryClient.invalidateQueries({ queryKey: ["/api/sgb/config"] });
    },
  });

  useEffect(() => {
    if (config) { setCfgEnabled(config.enabled ?? true); setCfgDays(String(config.intervalDays ?? 90)); }
  }, [config]);

  useEffect(() => {
    if (!syncActive) return;
    pollRef.current = setInterval(async () => {
      try {
        const s = await authFetch("/api/sgb/status");
        if (s.sync_log) {
          setLogLines(prev => {
            const last = prev[prev.length - 1];
            if (!last || last.msg !== s.sync_log) return [...prev, { ts: Date.now(), msg: s.sync_log }];
            return prev;
          });
        }
        if (!s.sync_ativo) {
          setSyncActive(false);
          queryClient.invalidateQueries({ queryKey: ["/api/sgb/status"] });
          queryClient.invalidateQueries({ queryKey: ["/api/sgb/sync-log"] });
          queryClient.invalidateQueries({ queryKey: ["/api/sgb/bacias"] });
          queryClient.invalidateQueries({ queryKey: ["/api/sgb/geocronologia/lista"] });
        }
      } catch {}
    }, 1000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [syncActive, queryClient]);

  useEffect(() => {
    if (!syncActive || !startTime) return;
    const t = setInterval(() => setElapsed(Math.round((Date.now() - startTime) / 1000)), 500);
    return () => clearInterval(t);
  }, [syncActive, startTime]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logLines]);

  useEffect(() => {
    if (status?.sync_ativo && !syncActive) {
      setSyncActive(true); setStartTime(Date.now());
      setLogLines([{ ts: Date.now(), msg: status.sync_log || "Sync em andamento..." }]);
    }
  }, [status?.sync_ativo]);

  const prog = status?.sync_progresso ?? 0;
  const tot = status?.sync_total ?? 0;
  const pct = tot > 0 ? Math.round((prog / tot) * 100) : 0;
  const rate = elapsed > 0 ? (prog / elapsed).toFixed(1) : "—";

  async function handleConsulta() {
    const lat = parseFloat(latConsulta), lon = parseFloat(lonConsulta);
    if (isNaN(lat) || isNaN(lon)) {
      toast({ title: "Erro", description: "Informe lat e lon válidos", variant: "destructive" }); return;
    }
    setConsultaLoading(true);
    try {
      const res = await authFetch(`/api/sgb/consulta-completa?lat=${lat}&lon=${lon}&raio=${raioConsulta}`);
      setConsultaResult(res);
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally { setConsultaLoading(false); }
  }

  const totalRecords = (status?.tabelas?.sgb_bacias_sedimentares?.total ?? 0) +
    (status?.tabelas?.sgb_geocronologia?.total ?? 0);

  return (
    <div className="space-y-6">
      <ModuleHeader
        icon={Layers}
        iconColor="text-blue-500"
        title="SGB — Dados Geológicos"
        description={`${fmt(status?.tabelas?.sgb_bacias_sedimentares?.total)} bacias · ${fmt(status?.tabelas?.sgb_geocronologia?.total)} geocronologia · Fonte: GeoServer SGB/CPRM`}
        recordCount={totalRecords}
        recordLabel="registros"
        lastUpdate={status?.ultima_sync}
        onRefresh={() => refetchStatus()}
        refreshing={statusFetching}
        docsHref="/docs"
        extra={
          <>
            {syncActive && (
              <Badge variant="default" className="animate-pulse bg-blue-500 text-white gap-1">
                <Activity className="h-3 w-3" /> AO VIVO — {status?.sync_layer}
              </Badge>
            )}
            <Button variant="default" size="sm" onClick={() => syncMutation.mutate("all")}
              disabled={syncMutation.isPending || syncActive} data-testid="button-sync-sgb-all">
              {syncMutation.isPending || syncActive ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
              Sync Completo
            </Button>
            <Button variant="outline" size="sm" onClick={() => syncMutation.mutate("bacias")}
              disabled={syncMutation.isPending || syncActive} data-testid="button-sync-sgb-bacias">
              <Waves className="h-4 w-4 mr-1" /> Só Bacias
            </Button>
            <Button variant="outline" size="sm" onClick={() => syncMutation.mutate("geocronologia")}
              disabled={syncMutation.isPending || syncActive} data-testid="button-sync-sgb-geo">
              <FlaskConical className="h-4 w-4 mr-1" /> Só Geocrono
            </Button>
          </>
        }
      />

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Waves className="h-4 w-4 text-blue-400" />
              <p className="text-muted-foreground text-xs uppercase">Bacias Sedimentares</p>
            </div>
            <p className="text-3xl font-bold text-blue-500" data-testid="stat-sgb-bacias">
              {fmt(status?.tabelas?.sgb_bacias_sedimentares?.total)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">polígonos nacionais</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <FlaskConical className="h-4 w-4 text-purple-400" />
              <p className="text-muted-foreground text-xs uppercase">Geocronologia</p>
            </div>
            <p className="text-3xl font-bold text-purple-500" data-testid="stat-sgb-geocrono">
              {fmt(status?.tabelas?.sgb_geocronologia?.total)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">amostras radiométricas</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-muted-foreground text-xs uppercase tracking-wide">Última Sync</p>
            <p className="text-sm font-semibold mt-2">{fmtDate(status?.ultima_sync)}</p>
            <p className="text-xs text-muted-foreground mt-1">Próxima: {fmtDate(status?.proxima_sync)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-muted-foreground text-xs uppercase tracking-wide">Status</p>
            {syncActive ? (
              <div className="flex items-center gap-1 mt-2 text-blue-500">
                <Activity className="h-4 w-4 animate-pulse" />
                <span className="text-sm font-semibold">{status?.sync_layer || "Sincronizando"}</span>
              </div>
            ) : totalRecords > 0 ? (
              <div className="flex items-center gap-1 mt-2 text-green-500">
                <CheckCircle className="h-4 w-4" /><span className="text-sm font-semibold">OK</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 mt-2 text-yellow-500">
                <Clock className="h-4 w-4" /><span className="text-sm font-semibold">Sem dados</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Live Monitor */}
      {syncActive && (
        <Card className="border-blue-500/40 bg-blue-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Zap className="h-4 w-4 text-blue-500" /> Monitor ao Vivo — {status?.sync_layer}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Progresso</span>
              <span className="font-mono font-semibold">{pct}% ({fmt(prog)}/{fmt(tot)})</span>
            </div>
            <Progress value={pct} className="h-2" />
            <div className="flex gap-6 text-xs text-muted-foreground">
              <span>⏱ {elapsed}s</span>
              <span>🚀 {rate} reg/s</span>
              <span>⌛ ETA: {fmtEta(elapsed, prog, tot)}</span>
            </div>
            <div ref={logRef} className="bg-black/60 rounded font-mono text-xs text-blue-400 p-3 h-32 overflow-y-auto">
              {logLines.map((l, i) => (
                <div key={i} className="whitespace-pre-wrap leading-5">
                  <span className="text-blue-700">[{new Date(l.ts).toLocaleTimeString("pt-BR")}]</span>{" "}{l.msg}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="consulta">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="consulta">Consulta por Ponto</TabsTrigger>
          <TabsTrigger value="bacias">Bacias Sedimentares</TabsTrigger>
          <TabsTrigger value="geocrono">Geocronologia</TabsTrigger>
          <TabsTrigger value="config">Config & Log</TabsTrigger>
        </TabsList>

        {/* Consulta completa por ponto */}
        <TabsContent value="consulta">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="h-4 w-4 text-blue-500" />
                Consulta Completa por Coordenada
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Latitude</Label>
                  <Input placeholder="-15.7801" value={latConsulta} onChange={e => setLatConsulta(e.target.value)} data-testid="input-sgb-lat" />
                </div>
                <div className="space-y-1">
                  <Label>Longitude</Label>
                  <Input placeholder="-47.9292" value={lonConsulta} onChange={e => setLonConsulta(e.target.value)} data-testid="input-sgb-lon" />
                </div>
                <div className="space-y-1">
                  <Label>Raio geocrono (km)</Label>
                  <Input placeholder="100" value={raioConsulta} onChange={e => setRaioConsulta(e.target.value)} data-testid="input-sgb-raio" />
                </div>
              </div>
              <Button onClick={handleConsulta} disabled={consultaLoading} data-testid="button-sgb-consulta">
                {consultaLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                Consultar
              </Button>

              {consultaResult && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  {/* Bacias */}
                  <Card className={consultaResult.bacias_sedimentares?.total > 0 ? "border-blue-400/50" : ""}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Waves className="h-4 w-4 text-blue-400" /> Bacias Sedimentares
                        <Badge variant={consultaResult.bacias_sedimentares?.total > 0 ? "default" : "outline"}>
                          {consultaResult.bacias_sedimentares?.total > 0 ? "SOBRE BACIA" : "Fora de bacia"}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {consultaResult.bacias_sedimentares?.data?.length === 0
                        ? <p className="text-sm text-muted-foreground">Ponto fora de bacias sedimentares</p>
                        : consultaResult.bacias_sedimentares?.data?.map((b: any) => (
                          <div key={b.id} className="border rounded p-2 mb-2" data-testid={`card-bacia-${b.id}`}>
                            <p className="font-semibold text-sm">{b.nome_bacia}</p>
                            <p className="text-xs text-muted-foreground">{b.tipo}</p>
                          </div>
                        ))}
                    </CardContent>
                  </Card>

                  {/* Geocronologia */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FlaskConical className="h-4 w-4 text-purple-400" />
                        Geocronologia Próxima ({consultaResult.geocronologia_proxima?.raio_km}km)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {consultaResult.geocronologia_proxima?.data?.length === 0
                        ? <p className="text-sm text-muted-foreground">Sem amostras no raio</p>
                        : <div className="space-y-1 max-h-48 overflow-y-auto">
                          {consultaResult.geocronologia_proxima?.data?.map((g: any) => (
                            <div key={g.id} className="border rounded px-2 py-1.5 text-xs" data-testid={`card-geocrono-${g.id}`}>
                              <div className="flex justify-between">
                                <span className="font-medium truncate">{g.rocha || "Rocha desconhecida"}</span>
                                <span className="text-muted-foreground ml-2 shrink-0">{g.distancia_km} km</span>
                              </div>
                              <p className="text-muted-foreground truncate">{g.metodos}</p>
                            </div>
                          ))}
                        </div>}
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bacias Sedimentares */}
        <TabsContent value="bacias">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Waves className="h-4 w-4 text-blue-500" />
                Bacias Sedimentares do Brasil — {fmt(bacias?.total)} registros
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {bacias?.data?.map((b: any) => (
                  <div key={b.id} className="flex items-center justify-between border rounded px-4 py-2" data-testid={`row-bacia-${b.id}`}>
                    <div>
                      <p className="font-semibold">{b.nome_bacia}</p>
                    </div>
                    <Badge variant="outline">{b.tipo}</Badge>
                  </div>
                ))}
                {!bacias?.data?.length && (
                  <p className="text-center text-muted-foreground py-8 text-sm">Sem dados — execute o sync</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Geocronologia */}
        <TabsContent value="geocrono">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-purple-500" />
                Geocronologia — {fmt(geoList?.total)} amostras
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Rocha</Label>
                  <Input placeholder="ex: Granito, Gnaisse..." value={geoRocha} onChange={e => { setGeoRocha(e.target.value); setGeoPage(0); }} data-testid="input-sgb-rocha" />
                </div>
                <div className="space-y-1">
                  <Label>Método</Label>
                  <Input placeholder="ex: U-Pb, Sm-Nd..." value={geoMetodo} onChange={e => { setGeoMetodo(e.target.value); setGeoPage(0); }} data-testid="input-sgb-metodo" />
                </div>
                <div className="space-y-1">
                  <Label>Busca livre</Label>
                  <Input placeholder="amostra, localidade..." value={geoBusca} onChange={e => { setGeoBusca(e.target.value); setGeoPage(0); }} data-testid="input-sgb-busca" />
                </div>
              </div>

              {geoLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="table-sgb-geocrono">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 pr-3 font-medium">Amostra</th>
                        <th className="pb-2 pr-3 font-medium">Rocha</th>
                        <th className="pb-2 pr-3 font-medium">Método</th>
                        <th className="pb-2 pr-3 font-medium">Material</th>
                        <th className="pb-2 font-medium">Coord</th>
                      </tr>
                    </thead>
                    <tbody>
                      {geoList?.data?.map((g: any) => (
                        <tr key={g.id} className="border-b hover:bg-muted/40 transition-colors" data-testid={`row-geocrono-${g.id}`}>
                          <td className="py-1.5 pr-3 font-mono text-xs font-bold text-purple-500">{g.amostra || "—"}</td>
                          <td className="py-1.5 pr-3 text-xs max-w-[160px] truncate" title={g.rocha}>{g.rocha || "—"}</td>
                          <td className="py-1.5 pr-3 text-xs max-w-[180px] truncate text-muted-foreground" title={g.metodos}>{g.metodos || "—"}</td>
                          <td className="py-1.5 pr-3 text-xs text-muted-foreground">{g.materiais_analisados || "—"}</td>
                          <td className="py-1.5 text-xs text-muted-foreground whitespace-nowrap">
                            {g.x != null ? `${Number(g.y).toFixed(3)}, ${Number(g.x).toFixed(3)}` : "—"}
                          </td>
                        </tr>
                      ))}
                      {geoList?.data?.length === 0 && (
                        <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Nenhuma amostra encontrada</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex items-center justify-between pt-1">
                <Button variant="outline" size="sm" disabled={geoPage === 0} onClick={() => setGeoPage(p => p - 1)} data-testid="button-sgb-prev">Anterior</Button>
                <span className="text-sm text-muted-foreground">Pág {geoPage + 1} · {fmt(geoList?.total)} amostras</span>
                <Button variant="outline" size="sm" disabled={!geoList || (geoPage + 1) * GEO_LIMIT >= (geoList?.total ?? 0)} onClick={() => setGeoPage(p => p + 1)} data-testid="button-sgb-next">Próxima</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Config & Log */}
        <TabsContent value="config">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Settings className="h-4 w-4" /> Scheduler</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div><Label>Sync automático</Label><p className="text-xs text-muted-foreground">Atualiza periodicamente</p></div>
                  <Switch checked={cfgEnabled} onCheckedChange={setCfgEnabled} data-testid="switch-sgb-enabled" />
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label>Intervalo (dias)</Label>
                  <Input type="number" min={1} value={cfgDays} onChange={e => setCfgDays(e.target.value)} data-testid="input-sgb-interval" />
                  <p className="text-xs text-muted-foreground">Dados geológicos são estáveis — 90 dias é o default</p>
                </div>
                <Button onClick={() => configMutation.mutate({ enabled: cfgEnabled, intervalDays: parseInt(cfgDays) || 90 })}
                  disabled={configMutation.isPending} data-testid="button-sgb-save-config">
                  {configMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salvar
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" /> Histórico</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {syncLog?.data?.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Nenhuma importação registrada</p>}
                  {syncLog?.data?.map((l: any) => (
                    <div key={l.id} className="border rounded px-3 py-2 text-sm" data-testid={`row-sgb-log-${l.id}`}>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{fmtDate(l.created_at)}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{l.tipo}</Badge>
                          {l.erro ? <Badge variant="destructive" className="text-xs">Erro</Badge> : <Badge className="bg-green-500 text-white text-xs">OK</Badge>}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex gap-3">
                        <span>{fmt(l.registros_importados)} registros</span>
                        <span>{fmtDur(l.duracao_seg)}</span>
                        {l.erro && <span className="text-red-400">{l.erro}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
